import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Copy, Check, DollarSign, Sparkles, Download } from "lucide-react";

export default function BulkGift() {
  const { user } = useAuth() || {};
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [ministryName, setMinistryName] = useState("");
  const [selectedTier, setSelectedTier] = useState("25");
  const [bundles, setBundles] = useState([]);
  const [openBundle, setOpenBundle] = useState(null);
  const [openCodes, setOpenCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => { api.get("/bulk-gifts/tiers").then(r => setTiers(r.data.tiers || [])); }, []);
  useEffect(() => { if (user) api.get("/me/bulk-bundles").then(r => setBundles(r.data)); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in first"); nav("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bulk-gifts/purchase", { tier: selectedTier, ministry_name: ministryName, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setLoading(false); }
  };

  const viewCodes = async (bundle) => {
    try {
      const { data } = await api.get(`/me/bulk-bundles/${bundle.id}/codes`);
      setOpenBundle(bundle); setOpenCodes(data.codes || []);
    } catch { toast.error("Failed to load codes"); }
  };

  const copy = (code) => { navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(""), 1600); toast.success("Copied"); };
  const copyAll = () => {
    const text = openCodes.map(c => c.code).join("\n");
    navigator.clipboard.writeText(text); toast.success(`Copied ${openCodes.length} codes`);
  };
  const downloadCsv = () => {
    const csv = "code,status,redeemed_by\n" + openCodes.map(c => `${c.code},${c.status},${c.redeemed_by_user_id || ""}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${openBundle.ministry_name.replace(/\s+/g,"_")}_gift_codes.csv`;
    a.click();
  };

  return (
    <div className="pt-16" data-testid="bulk-gift-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2 flex items-center gap-2"><Users className="w-3 h-3"/>Ministry Bulk Gifting</p>
        <h1 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-4">Bless Your Whole Congregation</h1>
        <p className="text-white/70 max-w-2xl mb-10">Pastors and ministry leaders — gift 12-month Premium memberships to your community at a per-seat discount. Distribute codes via email, Sunday bulletin, or your church app.</p>

        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {tiers.map(t => (
            <button key={t.key} onClick={()=>setSelectedTier(t.key)} className={`text-left rounded-2xl border-2 p-6 transition-all ${selectedTier === t.key ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_8px_30px_rgba(212,175,55,0.15)]" : "border-white/10 bg-[#0A0A0E] hover:border-white/25"}`} data-testid={`tier-${t.key}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Ministry Tier</p>
                <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded bg-[#D4AF37] text-[#050507]">SAVE {t.discount_pct}%</span>
              </div>
              <h3 className="font-heading text-3xl font-bold mb-1">{t.seats} seats</h3>
              <p className="text-xs text-white/50 mb-4">12-month Premium each</p>
              <div className="flex items-baseline gap-1"><DollarSign className="w-5 h-5 text-[#D4AF37]"/><span className="font-heading text-4xl font-bold text-[#D4AF37]">{t.price_usd.toFixed(0)}</span></div>
              <p className="text-xs text-white/50 mt-2">${t.per_seat_usd.toFixed(2)}/seat · one-time</p>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-8">
          <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-8">
            <h3 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#D4AF37]"/>Complete Purchase</h3>
            <form onSubmit={submit} className="space-y-3">
              <input required placeholder="Ministry or church name" value={ministryName} onChange={e=>setMinistryName(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="bulk-ministry-name"/>
              <p className="text-xs text-white/50 px-2">You'll receive {tiers.find(t=>t.key===selectedTier)?.seats || 0} gift codes to distribute after payment.</p>
              <button disabled={loading || !ministryName.trim()} className="w-full btn-gold py-3 rounded-full font-semibold disabled:opacity-60" data-testid="bulk-submit">
                {loading ? "Redirecting…" : `Purchase ${tiers.find(t=>t.key===selectedTier)?.seats || 0} seats for $${tiers.find(t=>t.key===selectedTier)?.price_usd.toFixed(0) || 0}`}
              </button>
              <p className="text-[11px] text-white/40 text-center pt-2">Codes are valid immediately after payment and never expire.</p>
            </form>
          </div>

          <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-8">
            <h3 className="font-heading text-2xl font-bold mb-4">Your Bundles</h3>
            {!user ? <p className="text-white/60 text-sm">Sign in to view your bundles.</p>
              : bundles.length === 0 ? <p className="text-white/60 text-sm" data-testid="no-bundles">No bundles yet.</p>
              : (
                <div className="space-y-3">
                  {bundles.map(b => (
                    <div key={b.id} className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl p-4" data-testid={`bundle-${b.id}`}>
                      <div>
                        <p className="font-heading font-semibold">{b.ministry_name}</p>
                        <p className="text-xs text-white/50">{b.seats} seats · {b.status === "paid" ? `$${b.price_usd.toFixed(0)} paid` : "Pending payment"}</p>
                        {b.status === "paid" && b.redeemed_count !== undefined && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden max-w-[160px]"><div className="h-full bg-[#D4AF37]" style={{width: `${Math.round((b.redeemed_count/b.seats)*100)}%`}}/></div>
                            <span className="text-[11px] text-[#D4AF37] font-semibold" data-testid={`redeemed-${b.id}`}>{b.redeemed_count} / {b.seats} redeemed</span>
                          </div>
                        )}
                      </div>
                      {b.status === "paid" && <button onClick={()=>viewCodes(b)} className="btn-ghost rounded-full px-4 py-1.5 text-xs font-semibold" data-testid={`view-codes-${b.id}`}>View codes →</button>}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>

        {openBundle && (
          <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setOpenBundle(null)} data-testid="codes-modal">
            <div onClick={e=>e.stopPropagation()} className="bg-[#0A0A0E] border border-[#D4AF37]/40 rounded-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-[#D4AF37]">{openBundle.ministry_name}</p>
                  <h3 className="font-heading text-2xl font-bold">{openCodes.length} Gift Codes</h3>
                </div>
                <div className="flex gap-2">
                  <button onClick={copyAll} className="btn-ghost rounded-full px-3 py-1.5 text-xs" data-testid="copy-all-codes"><Copy className="w-3 h-3 inline mr-1"/>Copy all</button>
                  <button onClick={downloadCsv} className="btn-ghost rounded-full px-3 py-1.5 text-xs" data-testid="download-csv"><Download className="w-3 h-3 inline mr-1"/>CSV</button>
                </div>
              </div>
              <div className="overflow-y-auto flex-1 space-y-1">
                {openCodes.map(c => (
                  <div key={c.code} className="flex items-center justify-between bg-black/40 rounded-lg px-3 py-2 border border-[#D4AF37]/20" data-testid={`code-${c.code}`}>
                    <code className="font-heading text-[#D4AF37] tracking-widest">{c.code}</code>
                    <div className="flex items-center gap-2">
                      {c.status === "redeemed" && <span className="text-[9px] font-bold tracking-widest uppercase bg-white/10 text-white/60 px-2 py-0.5 rounded">REDEEMED</span>}
                      <button onClick={()=>copy(c.code)} className="p-1.5 hover:bg-white/5 rounded">{copied === c.code ? <Check className="w-4 h-4 text-[#D4AF37]"/> : <Copy className="w-4 h-4 text-white/60"/>}</button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={()=>setOpenBundle(null)} className="mt-4 btn-ghost rounded-full py-2 text-sm" data-testid="close-codes-modal">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
